import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Search, ArrowRight, Loader, X } from 'lucide-react';
import { toast } from 'react-hot-toast';
import apiService from '../../services/api';
import Modal from './Modal';

const TransferModal = ({ isOpen, onClose, transferType, fromUser, onTransferComplete }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [searching, setSearching] = useState(false);
  const [transferring, setTransferring] = useState(false);

  useEffect(() => {
    if (searchTerm.length >= 1) {
      searchUsers();
    } else {
      setSearchResults([]);
    }
  }, [searchTerm]);

  const searchUsers = async () => {
    setSearching(true);
    try {
      const response = await apiService.get('/transfers/search', {
        params: { search: searchTerm, transfer_type: transferType },
      });
      const users = Array.isArray(response.data) ? response.data : [];
      setSearchResults(users.filter((user) => user.id !== fromUser.id));
    } catch (error) {
      console.error('Search error:', error);
      toast.error(error.response?.data?.error || 'Failed to search users');
    } finally {
      setSearching(false);
    }
  };

  const handleTransfer = async () => {
    if (!selectedUser || !reason.trim()) {
      toast.error('Please select a user and provide a reason');
      return;
    }

    setTransferring(true);
    try {
      const endpoint =
        transferType === 'team_lead' ? '/transfers/team-lead' : '/transfers/referral-partner';
      await apiService.post(endpoint, {
        from_user_id: fromUser.id,
        to_user_id: selectedUser.id,
        reason: reason.trim(),
      });

      toast.success(
        `${transferType === 'team_lead' ? 'Team lead' : 'Referral partner'} transferred successfully`
      );
      onTransferComplete();
      handleClose();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Transfer failed');
    } finally {
      setTransferring(false);
    }
  };

  const handleClose = () => {
    setSearchTerm('');
    setSearchResults([]);
    setSelectedUser(null);
    setReason('');
    onClose();
  };

  if (!fromUser) {
    return null;
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={`Transfer ${transferType === 'team_lead' ? 'Team Lead' : 'Referral Partner'}`}
    >
      <div style={{ textAlign: 'left' }}>
        {/* From User Info */}
        <div
          style={{
            padding: '1rem',
            background: '#f8fafc',
            borderRadius: '8px',
            marginBottom: '1.5rem',
          }}
        >
          <h4 style={{ margin: '0 0 0.5rem', color: '#374151' }}>Transferring from:</h4>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <div
              className='user-avatar'
              style={{ width: '32px', height: '32px', fontSize: '0.75rem' }}
            >
              {fromUser.name
                ?.split(' ')
                .map((n) => n[0])
                .join('') || '?'}
            </div>
            <div>
              <div style={{ fontWeight: '500' }}>{fromUser.name}</div>
              <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>{fromUser.email}</div>
            </div>
          </div>
        </div>

        {/* Search Users */}
        <div className='form-group'>
          <label>Search {transferType === 'team_lead' ? 'Team Lead' : 'Referral Partner'}</label>
          <div style={{ position: 'relative' }}>
            <input
              type='text'
              placeholder='Type name or email to search...'
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ paddingLeft: '2.5rem' }}
            />
            <Search
              size={16}
              style={{
                position: 'absolute',
                left: '0.75rem',
                top: '50%',
                transform: 'translateY(-50%)',
                color: '#6b7280',
              }}
            />
            {searching && (
              <Loader
                className='spin'
                size={16}
                style={{
                  position: 'absolute',
                  right: '0.75rem',
                  top: '50%',
                  transform: 'translateY(-50%)',
                }}
              />
            )}
          </div>
        </div>

        {/* Search Results */}
        {searchResults.length > 0 && (
          <div style={{ marginBottom: '1.5rem' }}>
            <label>Select User:</label>
            <div
              style={{
                maxHeight: '200px',
                overflowY: 'auto',
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
              }}
            >
              {searchResults.map((user) => (
                <motion.div
                  key={user.id}
                  style={{
                    padding: '0.75rem',
                    borderBottom: '1px solid #f3f4f6',
                    cursor: 'pointer',
                    background: selectedUser?.id === user.id ? '#eff6ff' : 'white',
                  }}
                  whileHover={{ backgroundColor: '#f9fafb' }}
                  onClick={() => setSelectedUser(user)}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                    <div
                      className='user-avatar'
                      style={{ width: '32px', height: '32px', fontSize: '0.75rem' }}
                    >
                      {user.name
                        ?.split(' ')
                        .map((n) => n[0])
                        .join('') || '?'}
                    </div>
                    <div>
                      <div style={{ fontWeight: '500' }}>{user.name}</div>
                      <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>{user.email}</div>
                    </div>
                    {selectedUser?.id === user.id && (
                      <div style={{ marginLeft: 'auto', color: '#3b82f6' }}>✓</div>
                    )}
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        )}

        {/* Selected User Preview */}
        {selectedUser && (
          <div
            style={{
              padding: '1rem',
              background: '#eff6ff',
              borderRadius: '8px',
              marginBottom: '1.5rem',
              border: '1px solid #bfdbfe',
            }}
          >
            <h4 style={{ margin: '0 0 0.5rem', color: '#1e40af' }}>Transferring to:</h4>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <div
                className='user-avatar'
                style={{ width: '32px', height: '32px', fontSize: '0.75rem' }}
              >
                {selectedUser.name
                  ?.split(' ')
                  .map((n) => n[0])
                  .join('') || '?'}
              </div>
              <div>
                <div style={{ fontWeight: '500' }}>{selectedUser.name}</div>
                <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>{selectedUser.email}</div>
              </div>
            </div>
          </div>
        )}

        {/* Reason */}
        <div className='form-group'>
          <label>Reason for Transfer *</label>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder='Please provide a reason for this transfer...'
            rows={3}
            required
          />
        </div>

        {/* Transfer Preview */}
        {selectedUser && (
          <div
            style={{
              padding: '1rem',
              background: '#fef3c7',
              borderRadius: '8px',
              marginBottom: '1.5rem',
              border: '1px solid #fbbf24',
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                marginBottom: '0.5rem',
              }}
            >
              <span style={{ fontWeight: '500', color: '#92400e' }}>Transfer Summary:</span>
            </div>
            <div style={{ fontSize: '0.875rem', color: '#92400e' }}>
              All data associated with <strong>{fromUser.name}</strong> will be transferred to{' '}
              <strong>{selectedUser.name}</strong>. This includes teams, tasks, admissions, and
              other related records.
            </div>
          </div>
        )}

        {/* Actions */}
        <div className='modal-actions'>
          <button type='button' className='btn-secondary' onClick={handleClose}>
            Cancel
          </button>
          <button
            type='button'
            className='btn-primary'
            onClick={handleTransfer}
            disabled={!selectedUser || !reason.trim() || transferring}
          >
            {transferring ? (
              <>
                <Loader className='spin' size={16} />
                Transferring...
              </>
            ) : (
              <>
                <ArrowRight size={16} />
                Transfer
              </>
            )}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default TransferModal;
